(() => {
  'use strict';

  const MIN_FILL_TIME = 2500;
  const MAX_LINKS = 4;
  const startedAt = new WeakMap();
  const challengeAnswers = new WeakMap();

  const isProtectedForm = (form) => {
    const type = form.querySelector('input[name="form_type"]')?.value;
    return type === 'contact' || type === 'customer' || type === 'new_comment';
  };

  const isEmailOnlyForm = (form) => form.querySelector('input[name="form_type"]')?.value === 'customer';

  const showMessage = (form, message) => {
    let notice = form.querySelector('[data-aomo-spam-message]');

    if (!notice) {
      notice = document.createElement('div');
      notice.className = 'aomo-spam-guard__message';
      notice.dataset.aomoSpamMessage = '';
      notice.setAttribute('role', 'alert');
      notice.setAttribute('aria-live', 'polite');
      form.append(notice);
    }

    notice.textContent = message;
    notice.hidden = false;
  };

  const addHumanChallenge = (form) => {
    const firstNumber = Math.floor(Math.random() * 6) + 2;
    const secondNumber = Math.floor(Math.random() * 6) + 1;
    const challenge = document.createElement('div');

    challenge.className = 'aomo-spam-guard__challenge';
    challenge.dataset.aomoHumanChallenge = '';
    challenge.innerHTML = `
      <div class="aomo-spam-guard__challenge-icon" aria-hidden="true">&#10003;</div>
      <label class="aomo-spam-guard__challenge-content">
        <span class="aomo-spam-guard__challenge-title">Human verification</span>
        <span class="aomo-spam-guard__challenge-question">I'm not a robot &mdash; what is ${firstNumber} + ${secondNumber}?</span>
        <input class="aomo-spam-guard__challenge-input" type="number" inputmode="numeric" min="0" max="20" step="1" required autocomplete="off" aria-label="Answer the human verification question">
      </label>
      <span class="aomo-spam-guard__challenge-note">Required</span>
    `;

    challengeAnswers.set(form, firstNumber + secondNumber);

    const submit = form.querySelector('button[type="submit"], input[type="submit"]');
    if (!submit) {
      form.append(challenge);
      return;
    }

    const submitParent = submit.parentElement;
    const groupedWithFields = submitParent && submitParent !== form && submitParent.querySelector('input:not([type="hidden"]), textarea, select');

    if (groupedWithFields) {
      submitParent.before(challenge);
    } else {
      submit.before(challenge);
    }
  };

  const protect = (form) => {
    if (form.dataset.aomoSpamGuard === 'true' || !isProtectedForm(form)) return;

    form.dataset.aomoSpamGuard = 'true';

    if (isEmailOnlyForm(form)) {
      const email = form.querySelector('input[name="contact[email]"]');
      if (email) {
        email.type = 'email';
        email.required = true;
      }
      return;
    }

    startedAt.set(form, Date.now());

    const trapWrap = document.createElement('div');
    trapWrap.className = 'aomo-spam-guard__trap';
    trapWrap.setAttribute('aria-hidden', 'true');
    trapWrap.innerHTML = '<label>Leave this field empty<input type="text" name="website" tabindex="-1" autocomplete="off" data-aomo-spam-trap></label>';
    form.append(trapWrap);
    addHumanChallenge(form);
  };

  const scan = (root = document) => {
    if (root instanceof HTMLFormElement) protect(root);
    root.querySelectorAll('form').forEach(protect);
  };

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.aomoSpamGuard !== 'true') return;

    if (isEmailOnlyForm(form)) {
      const email = form.querySelector('input[name="contact[email]"]');
      if (!email || !email.value.trim() || !email.validity.valid) {
        event.preventDefault();
        event.stopImmediatePropagation();
        showMessage(form, 'Please enter a valid email address.');
        email?.focus();
      }
      return;
    }

    const trap = form.querySelector('[data-aomo-spam-trap]');
    const elapsed = Date.now() - (startedAt.get(form) || Date.now());
    const body = form.querySelector('textarea[name="contact[body]"], textarea[name="comment[body]"]')?.value || '';
    const linkCount = (body.match(/https?:\/\/|www\./gi) || []).length;
    const challengeInput = form.querySelector('.aomo-spam-guard__challenge-input');
    const challengeAnswer = Number.parseInt(challengeInput?.value || '', 10);

    if (!challengeInput || challengeAnswer !== challengeAnswers.get(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showMessage(form, 'Please complete the human verification question correctly.');
      challengeInput?.focus();
      return;
    }

    if (trap?.value.trim() || elapsed < MIN_FILL_TIME || linkCount > MAX_LINKS) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showMessage(form, 'Please wait a moment, remove excessive links, and submit again.');
      startedAt.set(form, Date.now());
    }
  }, true);

  const boot = () => {
    const style = document.createElement('style');
    style.textContent = `
      .aomo-spam-guard__trap{position:absolute!important;left:-10000px!important;width:1px!important;height:1px!important;overflow:hidden!important}
      .aomo-spam-guard__challenge{display:flex;align-items:center;gap:12px;width:100%;margin:16px 0;padding:13px 15px;border:1px solid #c8dce7;border-radius:10px;background:#f4f9fc;color:#082f46;box-sizing:border-box;text-align:left}
      .aomo-spam-guard__challenge-icon{display:grid;place-items:center;flex:0 0 30px;width:30px;height:30px;border-radius:50%;background:#0b82bd;color:#fff;font-size:16px;font-weight:800;line-height:1}
      .aomo-spam-guard__challenge-content{display:grid;grid-template-columns:auto minmax(0,1fr) 68px;align-items:center;gap:8px 12px;min-width:0;flex:1;margin:0!important;color:inherit!important}
      .aomo-spam-guard__challenge-title{font-size:13px;font-weight:800;white-space:nowrap}
      .aomo-spam-guard__challenge-question{font-size:13px;line-height:1.35;color:#526e7d}
      .aomo-spam-guard__challenge-input{display:block!important;width:68px!important;min-width:68px!important;height:38px!important;min-height:38px!important;margin:0!important;padding:6px 9px!important;border:1px solid #9fbdcc!important;border-radius:7px!important;background:#fff!important;color:#082f46!important;font-size:16px!important;font-weight:700!important;line-height:1!important;text-align:center!important;box-shadow:none!important}
      .aomo-spam-guard__challenge-input:focus{outline:2px solid rgba(11,130,189,.24)!important;outline-offset:1px!important;border-color:#0b82bd!important}
      .aomo-spam-guard__challenge-note{flex:0 0 auto;font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:#6c8491}
      .aomo-spam-guard__message{margin-top:14px;padding:11px 13px;border:1px solid #e5b7b7;border-radius:8px;background:#fff3f3;color:#9f2525;font-size:13px;line-height:1.45}
      .aomo-spam-guard__message[hidden]{display:none}
      @media(max-width:700px){.aomo-spam-guard__challenge{align-items:flex-start;gap:10px}.aomo-spam-guard__challenge-content{grid-template-columns:1fr 64px}.aomo-spam-guard__challenge-title{grid-column:1/-1}.aomo-spam-guard__challenge-question{font-size:12px}.aomo-spam-guard__challenge-note{display:none}.aomo-spam-guard__challenge-input{width:64px!important;min-width:64px!important}}
    `;
    document.head.append(style);
    scan();

    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) scan(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
